import React, { useEffect, useState } from 'react';
import { 
  Search, Loader2, X, Phone, ClipboardList, 
  ShieldCheck, AlertCircle, User, Calendar 
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';
import { parsePositiveInt, validateUserRole } from '../../utils/inputValidation';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    handleSearch(search);
  }, [search, users]);

  const fetchUsers = async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from('users')
      .select('id, first_name, last_name, phone, role, created_at, is_verified')
      .order('id', { ascending: false });

    if (error) {
      console.error('Error cargando usuarios:', error);
      setLoading(false);
      return;
    }

    setUsers(data);
    setFiltered(data);
    setLoading(false);
  };

  const fetchUserTransactions = async (userId) => {
    setLoadingTx(true);

    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', userId)
      .order('id', { ascending: false });

    if (error) {
      console.error("Error cargando envíos:", error);
      setLoadingTx(false);
      return;
    }

    setTransactions(data);
    setLoadingTx(false);
  };

  const openModal = (user) => {
    setSelectedUser(user);
    fetchUserTransactions(user.id);
  };

  const closeModal = () => {
    setSelectedUser(null);
    setTransactions([]);
  };

  const handleSearch = (text) => {
    setSearch(text);

    if (!text.trim()) {
      setFiltered(users);
      return;
    }

    const filteredResults = users.filter(u =>
      (u.first_name + ' ' + u.last_name).toLowerCase().includes(text.toLowerCase()) ||
      u.phone.includes(text)
    );

    setFiltered(filteredResults);
  };

  const toggleVerification = async (userId, currentStatus) => {
    const safeUserId = parsePositiveInt(userId, 'ID de usuario');
    const { error } = await supabase.rpc('admin_update_user_verification', {
      p_user_id: safeUserId,
      p_is_verified: !currentStatus
    });

    if (error) {
      alert("Error al actualizar estado de verificación");
      console.error(error);
      return;
    }

    setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_verified: !currentStatus } : u));
    setFiltered(prev => prev.map(u => u.id === userId ? { ...u, is_verified: !currentStatus } : u));
  };

  const handleRoleChange = async (userId, newRole) => {
    const safeUserId = parsePositiveInt(userId, 'ID de usuario');
    const safeRole = validateUserRole(newRole);
    const { error } = await supabase.rpc('admin_update_user_role', {
      p_user_id: safeUserId,
      p_role: safeRole
    });

    if (error) {
      alert("Error actualizando el rol");
      console.error(error);
      return;
    }

    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    setFiltered(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
  };

  return (
    <div className="p-2 md:p-6 animate-in fade-in duration-500">
      
      {/* HEADER Y BUSCADOR */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
            <User className="w-6 h-6 text-blue-600" /> Gestión de Usuarios
          </h1>
          <p className="text-slate-500 text-sm font-medium mt-1">Administra accesos y verificaciones KYC</p>
        </div>

        <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-2xl shadow-sm border border-slate-100 w-full md:w-80 transition-all focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-500/10">
          <Search className="w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o teléfono..."
            className="flex-1 outline-none text-sm font-medium text-slate-700 placeholder-slate-400 bg-transparent"
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />
        </div>
      </div>

      {/* ESTADO DE CARGA */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[32px] border border-slate-100 shadow-sm">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
          <p className="text-slate-500 font-bold">Cargando base de datos...</p>
        </div>
      ) : (
        /* TABLA DE USUARIOS */
        <div className="bg-white rounded-[32px] border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[800px]">
              <thead>
                <tr className="text-slate-400 text-[10px] font-black uppercase tracking-widest border-b bg-slate-50/50">
                  <th className="py-5 pl-8">Usuario</th>
                  <th className="py-5">Contacto</th>
                  <th className="py-5">KYC (Verificación)</th>
                  <th className="py-5">Rol de Sistema</th>
                  <th className="py-5">Registro</th>
                  <th className="py-5 pr-8 text-right">Acción</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-50">
                {filtered.map(user => (
                  <tr
                    key={user.id}
                    className="group hover:bg-blue-50/30 transition-colors cursor-pointer"
                    onClick={() => openModal(user)}
                  >
                    {/* Nombre e ID */}
                    <td className="py-4 pl-8">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-black text-sm shrink-0">
                          {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-bold text-slate-800 capitalize leading-tight">
                            {user.first_name} {user.last_name}
                          </p>
                          <p className="text-[10px] text-slate-400 font-mono mt-0.5">ID: {user.id}</p>
                        </div>
                      </div>
                    </td>

                    {/* Teléfono */}
                    <td className="py-4 font-mono text-sm text-slate-600">
                      {user.phone}
                    </td>

                    {/* Toggle de Verificación KYC */}
                    <td className="py-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleVerification(user.id, user.is_verified);
                        }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 border ${
                          user.is_verified 
                            ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' 
                            : 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100'
                        }`}
                        title="Haz clic para cambiar estado"
                      >
                        {user.is_verified ? <ShieldCheck className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                        {user.is_verified ? 'Verificado' : 'Pendiente'}
                      </button>
                    </td>

                    {/* Selector de Rol */}
                    <td className="py-4">
                      <select
                        value={user.role}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        className={`text-xs font-bold px-3 py-1.5 rounded-lg outline-none cursor-pointer border transition-colors ${
                          user.role === "administrador"
                            ? "bg-purple-50 text-purple-700 border-purple-200"
                            : "bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <option value="usuario">Usuario Regular</option>
                        <option value="administrador">Administrador</option>
                      </select>
                    </td>

                    {/* Fecha Registro */}
                    <td className="py-4 text-xs font-medium text-slate-500">
                      {new Date(user.created_at).toLocaleDateString('es-CO')}
                    </td>

                    {/* WhatsApp */}
                    <td className="py-4 pr-8 text-right">
                      <button
                        className="inline-flex items-center justify-center p-2.5 bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 rounded-xl transition-colors"
                        title="Contactar por WhatsApp"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`https://wa.me/${user.phone}`, "_blank");
                        }}
                      >
                        <Phone className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}

                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-slate-400">
                        <Search className="w-8 h-8 mb-3 opacity-50" />
                        <p className="font-bold text-sm">No se encontraron usuarios con "{search}"</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================== MODAL DE DETALLES DEL USUARIO ================== */}
      <AnimatePresence>
        {selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white w-full max-w-2xl rounded-[32px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              {/* Cabecera del Modal */}
              <div className="p-6 md:p-8 bg-slate-50 border-b border-slate-100 relative">
                <button
                  onClick={closeModal}
                  className="absolute top-6 right-6 p-2 bg-white hover:bg-slate-100 text-slate-400 rounded-full transition-colors shadow-sm"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-[20px] bg-blue-600 text-white flex items-center justify-center font-black text-2xl shadow-lg shadow-blue-600/20">
                    {selectedUser.first_name?.charAt(0)}{selectedUser.last_name?.charAt(0)}
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-slate-800 tracking-tight capitalize">
                      {selectedUser.first_name} {selectedUser.last_name}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-mono text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-200">
                        {selectedUser.phone}
                      </span>
                      <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${selectedUser.is_verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-600'}`}>
                        {selectedUser.is_verified ? 'Verificado' : 'No Verificado'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                  <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4"/> {selectedUser.role}</span>
                  <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4"/> Registro: {new Date(selectedUser.created_at).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Contenido (Historial de transacciones) */}
              <div className="p-6 md:p-8 flex-1 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-black text-slate-800 text-lg flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                    Historial de Envíos
                  </h3>
                  <a
                    href={`https://wa.me/${selectedUser.phone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all shadow-sm"
                  >
                    <Phone className="w-4 h-4" /> Contactar
                  </a>
                </div>

                {loadingTx ? (
                  <div className="flex flex-col items-center justify-center py-10">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-2" />
                    <p className="text-slate-400 text-sm font-bold">Buscando envíos...</p>
                  </div>
                ) : transactions.length > 0 ? (
                  <div className="space-y-3">
                    {transactions.map(tx => (
                      <div key={tx.id} className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">
                            {new Date(tx.created_at).toLocaleDateString()} • ID: {tx.id} {/* 🔥 CORREGIDO: Sin el .split() */}
                          </p>
                          <p className="font-bold text-slate-800">
                            {tx.amount} <span className="text-slate-400 font-normal mx-1">→</span> <span className="text-green-600">{tx.received_amount}</span>
                          </p>
                          <p className="text-xs font-bold text-slate-500 mt-1">
                            {tx.origin_country} a {tx.destination_country}
                          </p>
                        </div>
                        <div className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest self-start sm:self-auto ${
                          tx.status?.toLowerCase() === 'completado' ? 'bg-green-100 text-green-700' :
                          tx.status?.toLowerCase() === 'cancelada' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {tx.status}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                    <p className="text-slate-500 font-bold">Este usuario aún no tiene envíos registrados.</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Users;

